import { forwardRef, useImperativeHandle, useRef } from 'react';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Product } from '@shared/components/ui/Product';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faBagShopping } from '@fortawesome/pro-light-svg-icons';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { useTranslation } from 'react-i18next';

export interface ProductLine {
  name: string;
  image: string;
  badge: string;
  qty: number;
  price: number;
  subtotal: number;
}

export interface ProductsModalProps {
  orderNumber: string;
  stockMessage: string;
  products: ProductLine[];
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
}

export interface ProductsModalRef {
  open: () => void;
  close: () => void;
}

export const ProductsModal = forwardRef<ProductsModalRef, ProductsModalProps>((props, ref) => {
  const modalRef = useRef<ModalCenterRef>(null);
  const { t } = useTranslation('ordersShippedModule');

  useImperativeHandle(ref, () => ({
    open: () => {
      modalRef.current?.openModal();
    },
    close: () => {
      modalRef.current?.closeModal();
    },
  }));

  return (
    <ModalCenter
      ref={modalRef}
      title={
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faBagShopping} className="w-4 h-4" />
          {props.orderNumber}
        </span>
      }
      type="default"
      width="lg"
      fullScreenMobile={true}
    >
      {/* Lista de productos */}
      <div className="space-y-4 max-h-[50vh] overflow-y-auto">
        {props.products.map((p, i) => (
          <div key={i} className="flex items-center justify-between gap-2 py-4 border-b border-card-border">
            <Product
              id={i}
              src={p.image}
              name={p.name}
              code={p.badge}
              acronym={p.name?.[0] || '?'}
              className="min-w-0"
              enableLineClamp={false}
            />
            <div className="bg-card-border rounded-full px-3 py-1 text-xs">{p.qty}</div>
          </div>
        ))}
      </div>
    </ModalCenter>
  );
});

ProductsModal.displayName = 'ProductsModal';
